import React, { useCallback, useEffect, useRef, useState } from 'react';

import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

// Mesmo visual das abas da tela Documentos: a aba selecionada é branca e se funde com o
// conteúdo logo abaixo; as demais ficam cinza, apagadas; o número vira uma bolinha no canto.
const useStyles = makeStyles({
  faixa: {
    padding: '12px 16px 0',
    borderBottom: '1px solid #e2e8f0',
  },
  abas: {
    // sem corte, para a bolinha do contador passar da borda da aba (desktop).
    overflow: 'visible',
    minHeight: 42,
  },
  scroller: { overflow: 'visible !important' },
  aba: {
    overflow: 'visible',
    minHeight: 42,
    minWidth: 130,
    margin: '0 4px 0 0',
    textTransform: 'none',
    fontWeight: 600,
    opacity: 1,
    borderRadius: '10px 10px 0 0',
    border: '1px solid #e2e8f0',
    borderBottom: 'none',
    backgroundColor: '#f1f5f9',
    color: '#64748b',
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
      gap: 6,
      opacity: 0.6,
      transition: 'opacity 0.15s',
    },
    '&:hover': { backgroundColor: '#f8fafc' },
    '&:hover .MuiTab-wrapper': { opacity: 1 },
    '&.Mui-selected': {
      backgroundColor: '#fff',
      color: '#0f172a',
      // desce 1px sobre a linha da base da faixa: some a divisória sob a aba.
      marginBottom: -1,
      paddingBottom: 1,
      zIndex: 1,
    },
    '&.Mui-selected .MuiTab-wrapper': { opacity: 1 },
  },
  bolinha: {
    position: 'absolute',
    top: 0,
    right: 0,
    transform: 'translate(35%, -40%)',
    minWidth: 20,
    height: 20,
    padding: '0 5px',
    boxSizing: 'border-box',
    borderRadius: 10,
    border: '2px solid #fff',
    backgroundColor: 'var(--cor-aba)',
    color: '#fff',
    fontSize: '0.6875rem',
    fontWeight: 700,
    lineHeight: '16px',
    textAlign: 'center',
    zIndex: 1,
  },
  // Celular: faixa rolável própria (o Tabs do Material-UI corta a bolinha e esconde as setas).
  // A linha da base fica num pseudo-elemento e a aba selecionada desce sobre ela.
  faixaCel: {
    position: 'relative',
    padding: '8px 0 0',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 1,
      backgroundColor: '#e2e8f0',
    },
  },
  rolagem: {
    display: 'flex',
    overflowX: 'auto',
    padding: '0 8px 1px',
    gap: 4,
    scrollbarWidth: 'none',
    scrollSnapType: 'x proximity',
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  abaCel: {
    flex: '0 0 auto',
    scrollSnapAlign: 'center',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minHeight: 42,
    padding: '0 12px',
    marginBottom: 1,
    whiteSpace: 'nowrap',
    font: 'inherit',
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none',
    cursor: 'pointer',
    borderRadius: '10px 10px 0 0',
    border: '1px solid #e2e8f0',
    borderBottom: 'none',
    backgroundColor: '#f1f5f9',
    color: '#64748b',
    '& > .conteudo': { opacity: 0.7 },
  },
  abaCelSelecionada: {
    marginBottom: 0,
    paddingBottom: 1,
    backgroundColor: '#fff',
    color: '#0f172a',
    zIndex: 1,
    '& > .conteudo': { opacity: 1 },
  },
  bolinhaCel: {
    minWidth: 20,
    height: 20,
    padding: '0 6px',
    boxSizing: 'border-box',
    borderRadius: 10,
    backgroundColor: 'var(--cor-aba)',
    color: '#fff',
    fontSize: '0.6875rem',
    fontWeight: 700,
    lineHeight: '20px',
    textAlign: 'center',
  },
  // Aviso de que há mais abas: degradê na borda com uma seta que rola a faixa.
  avisoMais: {
    position: 'absolute',
    top: 8,
    bottom: 1,
    width: 40,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  avisoEsq: {
    left: 0,
    justifyContent: 'flex-start',
    background: 'linear-gradient(to right, #fff 55%, rgba(255,255,255,0))',
  },
  avisoDir: {
    right: 0,
    justifyContent: 'flex-end',
    background: 'linear-gradient(to left, #fff 55%, rgba(255,255,255,0))',
  },
  setaAviso: { pointerEvents: 'auto', padding: 4 },
});

// Faixa de abas para o celular: rolagem com toque, aba selecionada sempre à vista e setas/degradê
// nas bordas que ainda têm abas escondidas.
const AbasCelular = ({ abas, valor, onChange }) => {
  const classes = useStyles();
  const rolagem = useRef(null);
  const [mais, setMais] = useState({ esquerda: false, direita: false });

  const atualizar = useCallback(() => {
    const el = rolagem.current;
    if (!el) return;
    setMais({
      esquerda: el.scrollLeft > 2,
      direita: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
    });
  }, []);

  useEffect(() => {
    atualizar();
    window.addEventListener('resize', atualizar);
    return () => window.removeEventListener('resize', atualizar);
  }, [atualizar, abas.length]);

  // Ao trocar de aba (ou abrir a tela), traz a selecionada para a vista.
  useEffect(() => {
    const el = rolagem.current;
    const aba = el && el.querySelector('[aria-selected="true"]');
    if (aba) {
      el.scrollTo({
        left: aba.offsetLeft - (el.clientWidth - aba.offsetWidth) / 2,
        behavior: 'smooth',
      });
    }
  }, [valor]);

  const rolar = (sentido) =>
    rolagem.current.scrollBy({
      left: sentido * rolagem.current.clientWidth * 0.6,
      behavior: 'smooth',
    });

  return (
    <Box className={classes.faixaCel}>
      <div
        ref={rolagem}
        className={classes.rolagem}
        onScroll={atualizar}
        role="tablist"
      >
        {abas.map((aba) => {
          const selecionada = aba.id === valor;
          return (
            <button
              key={aba.id}
              type="button"
              role="tab"
              aria-selected={selecionada}
              className={`${classes.abaCel} ${
                selecionada ? classes.abaCelSelecionada : ''
              }`}
              onClick={() => onChange(aba.id)}
            >
              <span
                className="conteudo"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {aba.icon && (
                  <span style={{ display: 'inline-flex', color: aba.cor }}>
                    {aba.icon}
                  </span>
                )}
                {aba.name}
              </span>
              {aba.contador != null && (
                <span
                  className={classes.bolinhaCel}
                  style={{ '--cor-aba': aba.cor }}
                >
                  {aba.contador > 999 ? '999+' : aba.contador}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {mais.esquerda && (
        <div className={`${classes.avisoMais} ${classes.avisoEsq}`}>
          <IconButton
            size="small"
            className={classes.setaAviso}
            aria-label="Abas anteriores"
            onClick={() => rolar(-1)}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </div>
      )}
      {mais.direita && (
        <div className={`${classes.avisoMais} ${classes.avisoDir}`}>
          <IconButton
            size="small"
            className={classes.setaAviso}
            aria-label="Mais abas"
            onClick={() => rolar(1)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </div>
      )}
    </Box>
  );
};

/**
 * Abas no estilo de aba de navegador. Controlado: quem usa guarda a aba selecionada.
 *
 * abas: [{ id, name, cor, icon?, contador? }] (`cor` colore o ícone e a bolinha do contador)
 * valor: id da aba selecionada; onChange(id).
 */
const AbasNavegador = ({ abas, valor, onChange }) => {
  const classes = useStyles();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  if (isMobile)
    return <AbasCelular abas={abas} valor={valor} onChange={onChange} />;
  return (
    <Box className={classes.faixa}>
      <Tabs
        value={valor}
        onChange={(event, id) => onChange(id)}
        className={classes.abas}
        classes={{ scroller: classes.scroller }}
        variant="standard"
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        {abas.map((aba) => (
          <Tab
            key={aba.id}
            value={aba.id}
            className={classes.aba}
            label={
              <>
                {aba.icon && (
                  <span style={{ display: 'inline-flex', color: aba.cor }}>
                    {aba.icon}
                  </span>
                )}
                {aba.name}
                {aba.contador != null && (
                  <span
                    className={classes.bolinha}
                    style={{ '--cor-aba': aba.cor }}
                  >
                    {aba.contador > 9999 ? '9999+' : aba.contador}
                  </span>
                )}
              </>
            }
          />
        ))}
      </Tabs>
    </Box>
  );
};

export default AbasNavegador;
